import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { type ProcessorDetails, useProcessorById, useUpdateProcessor } from '@/lib/api/processors'
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id'
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { SecretInput } from '@/components/form/secret-input'
import { useToast } from '@/components/ui/use-toast'
import { ProcessorEditHeader } from '../../components/processor-edit-header'
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field'
import {
  Universal3dsSettings,
  hasAcquirerBin,
  universal3dsSettingsFromOptions,
  universal3dsSettingsToOptions,
  type Universal3dsSettingsValue,
} from '../../components/universal-3ds-settings'

const DESCRIPTOR_MAX_LENGTH = 50

interface NetValveFormState {
  name: string
  enabled: boolean
  apiKey: string
  clientId: string
  netvalveMidId: string
  siteId: string
  descriptor: string
  testMode: boolean
  dryMode: boolean
  threeDS: Universal3dsSettingsValue
}

const readString = (options: Record<string, unknown> | null | undefined, key: string): string => {
  const value = options?.[key]
  return typeof value === 'string' ? value : ''
}

const readDescriptor = (options: Record<string, unknown> | null | undefined): string => {
  const descriptor = options?.descriptor
  if (typeof descriptor !== 'object' || descriptor === null || !('descriptor' in descriptor)) return ''
  return typeof descriptor.descriptor === 'string' ? descriptor.descriptor : ''
}

const formStateFromProcessor = (processor: ProcessorDetails): NetValveFormState => {
  const options = processor.options
  return {
    name: processor.name,
    enabled: processor.enabled ?? true,
    apiKey: readString(options, 'apiKey'),
    clientId: readString(options, 'clientId'),
    netvalveMidId: readString(options, 'netvalveMidId'),
    siteId: readString(options, 'siteId'),
    descriptor: readDescriptor(options),
    testMode: options?.testMode === true,
    dryMode: options?.dryMode === true,
    threeDS: universal3dsSettingsFromOptions(options),
  }
}

const NetValveEditPageInner = () => {
  const id = useProcessorEditId()
  const { t } = useTranslation('processors')
  const { data: processor, isLoading } = useProcessorById(id)

  if (isLoading) {
    return <div className="container mx-auto py-8" />
  }

  if (!processor || processor.type !== 'netvalve') {
    return (
      <div className="container mx-auto py-8">
        <Alert variant="destructive">
          <AlertDescription>{t('processorNotFound')}</AlertDescription>
        </Alert>
      </div>
    )
  }

  return <NetValveEditForm processor={processor} />
}

type Props = {
  processor: ProcessorDetails
}

const NetValveEditForm = ({ processor }: Props) => {
  const navigate = useNavigate()
  const { t } = useTranslation('processors')
  const { toast } = useToast()
  const processorExit = useProcessorEditExit()
  const updateProcessorMutation = useUpdateProcessor()

  const [validationError, setValidationError] = useState<string | null>(null)
  const [formData, setFormData] = useState<NetValveFormState>(() => formStateFromProcessor(processor))

  const validate = (): string | null => {
    if (!formData.name.trim()) return t('nameRequired')
    if (!formData.apiKey.trim()) return t('netvalve.validation.apiKeyRequired')
    if (!formData.clientId.trim()) return t('netvalve.validation.clientIdRequired')
    if (!formData.netvalveMidId.trim() && !formData.siteId.trim()) return t('netvalve.validation.midOrSiteRequired')
    if (formData.threeDS.enable3DS && !hasAcquirerBin(formData.threeDS)) return t('atLeastOneBinRequired')
    return null
  }

  const handleSubmit = async () => {
    const error = validate()
    setValidationError(error)
    if (error) return

    try {
      await updateProcessorMutation.mutateAsync({
        processorId: processor.id,
        updatedData: {
          type: processor.type,
          name: formData.name,
          enabled: formData.enabled,
          options: {
            apiKey: formData.apiKey.trim(),
            clientId: formData.clientId.trim(),
            netvalveMidId: formData.netvalveMidId.trim(),
            siteId: formData.siteId.trim(),
            descriptor: { descriptor: formData.descriptor.trim() },
            testMode: formData.testMode,
            dryMode: formData.dryMode,
            ...universal3dsSettingsToOptions(formData.threeDS),
          },
        },
      })

      toast({ description: t('processorUpdated') })
      if (processorExit.isEmbed) {
        processorExit.exitAfterSave()
        return
      }
      navigate(-1)
    } catch {
      toast({ description: t('failedToSave'), variant: 'destructive' })
    }
  }

  const handleCancel = () => {
    if (processorExit.isEmbed) {
      processorExit.exitCancel()
      return
    }
    navigate(-1)
  }

  return (
    <div className="container mx-auto py-8">
      <ProcessorEditHeader
        processorId={processor.id}
        title={t('netvalve.editTitle')}
        onCancel={handleCancel}
        onSave={handleSubmit}
        isSaving={updateProcessorMutation.isPending}
      />

      {validationError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('generalSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">{t('processorName')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked === true })}
              />
              <Label htmlFor="enabled">{t('enabled')}</Label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('apiCredentials')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="text-sm text-blue-700">
                <strong>{t('netvalve.credentials.howToGetTitle')}</strong>
              </p>
              <ol className="mt-2 list-decimal pl-4 text-sm text-blue-700">
                <li>{t('netvalve.credentials.step1')}</li>
                <li>
                  <Trans i18nKey="netvalve.credentials.step2" ns="processors" components={{ b: <b /> }} />
                </li>
                <li>{t('netvalve.credentials.step3')}</li>
                <li>{t('netvalve.credentials.step4')}</li>
              </ol>
            </div>

            <SecretInput
              label={t('netvalve.credentials.apiKeyLabel') + ' *'}
              value={formData.apiKey}
              onChange={(value) => setFormData({ ...formData, apiKey: value })}
              placeholder="nv_..."
            />
            <SecretInput
              label={t('netvalve.credentials.clientIdLabel') + ' *'}
              value={formData.clientId}
              onChange={(value) => setFormData({ ...formData, clientId: value })}
              placeholder="1001"
            />

            <div>
              <Label htmlFor="netvalveMidId">{t('netvalve.credentials.midIdLabel')}</Label>
              <Input
                id="netvalveMidId"
                value={formData.netvalveMidId}
                onChange={(e) => setFormData({ ...formData, netvalveMidId: e.target.value })}
                placeholder="f8c2c3a9-9d2e-4a7e-9c6b-6b3c0a6e4f21"
                className="font-mono"
              />
              <p className="text-muted-foreground mt-1 text-xs">{t('netvalve.credentials.midIdDescription')}</p>
            </div>

            <div>
              <Label htmlFor="siteId">{t('netvalve.credentials.siteIdLabel')}</Label>
              <Input
                id="siteId"
                value={formData.siteId}
                onChange={(e) => setFormData({ ...formData, siteId: e.target.value })}
                className="font-mono"
              />
              <p className="text-muted-foreground mt-1 text-xs">{t('netvalve.credentials.siteIdDescription')}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('descriptorSettings')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Label htmlFor="descriptor">{t('statementDescriptor')}</Label>
            <Input
              id="descriptor"
              value={formData.descriptor}
              onChange={(e) => setFormData({ ...formData, descriptor: e.target.value })}
              maxLength={DESCRIPTOR_MAX_LENGTH}
              placeholder="YOURCOMPANY.COM"
            />
            <p className="text-muted-foreground mt-1 text-xs">{t('netvalve.descriptorDescription')}</p>
          </CardContent>
        </Card>

        <Universal3dsSettings
          value={formData.threeDS}
          onChange={(threeDS) => setFormData({ ...formData, threeDS })}
        />

        <Card>
          <CardHeader>
            <CardTitle>{t('testModeSettings')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="testMode"
                checked={formData.testMode}
                onCheckedChange={(checked) => setFormData({ ...formData, testMode: checked === true })}
              />
              <Label htmlFor="testMode">{t('enableTestMode')}</Label>
            </div>
          </CardContent>
        </Card>

        <ProcessorDryModeField
          checked={formData.dryMode}
          onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
          disabled={updateProcessorMutation.isPending}
        />
      </div>
    </div>
  )
}

export const NetValveEditPage = withRequiredPermission(NetValveEditPageInner, PERMISSIONS.PROCESSORS_MANAGE)
